/**
 * One section of the review case (YAG-440): the cf-sect kicker made a
 * control. A collapsible section wears the SAME mono uppercase title row and
 * hairline as the static "THE ASK" kicker, plus a disclosure triangle and an
 * optional summary (data, not labels — counts, a cost line — so the closed
 * page reads at a glance). The static variant renders the plain kicker so
 * every section title on the page comes from the one component.
 */
import { useState, type ReactNode } from "react";

export function CaseSection({
  title,
  summary,
  defaultOpen = false,
  collapsible = true,
  children,
  testId,
}: {
  title: string;
  /** Right-aligned mono facts on the closed row; null says nothing. */
  summary?: string | null;
  defaultOpen?: boolean;
  /**
   * False renders the same kicker row without the disclosure — for sections
   * that must always be on the page (Specialists) while keeping every section
   * title in the one voice.
   */
  collapsible?: boolean;
  children: ReactNode;
  testId?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (!collapsible) {
    return (
      <section className="rvc-section" data-testid={testId}>
        <div className="cf-sect">
          <span className="rvc-sect-title">{title}</span>
          {summary ? <span className="rvc-sect-sum">{summary}</span> : null}
        </div>
        <div className="rvc-sect-body">{children}</div>
      </section>
    );
  }
  return (
    <section className="rvc-section" data-testid={testId}>
      <button
        type="button"
        className={"cf-sect rvc-sect-toggle" + (open ? " is-open" : "")}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="rvc-sect-tri" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        <span className="rvc-sect-title">{title}</span>
        {summary ? <span className="rvc-sect-sum">{summary}</span> : null}
      </button>
      {open && <div className="rvc-sect-body">{children}</div>}
    </section>
  );
}

/** The non-collapsible kicker ("THE ASK") in the same voice. */
export function CaseSectionTitle({ title }: { title: string }) {
  return <div className="cf-sect">{title}</div>;
}
